import { Component } from '@angular/core';
import { IonicSafeString, ToastController } from '@ionic/angular';
import { ToastButton } from '@ionic/core/dist/types/components/toast/toast-interface';
import { close } from 'ionicons/icons';
import { concatMap, from, map, Observable, of, take } from 'rxjs';

import { BaseAlertsOutletComponent } from './base-alerts-outlet.component';
import { AlertsService } from './alerts.service';
import { IAlert } from './alerts.interfaces';
import { v4 } from 'uuid';

@Component({
  selector: 'ionic-alerts-outlet',
  template: '',
})
export class IonicAlertsOutletComponent extends BaseAlertsOutletComponent {
  private currentToasts: HTMLIonToastElement[] = [];

  constructor(
    service: AlertsService,
    private readonly toastController: ToastController,
  ) {
    super(service);
  }

  dismissAllAlerts(): void {
    for (const toast of this.currentToasts) {
      void toast.dismiss();
    }
    this.currentToasts = [];
  }

  getToastHeight(toast: HTMLIonToastElement): number {
    let offsetTop = 0;
    if (!toast.shadowRoot) {
      return offsetTop;
    }

    const childNodes = Array.from(toast.shadowRoot.childNodes);
    childNodes.forEach((child) => {
      const box = (child as HTMLElement).getBoundingClientRect();
      offsetTop += box.height + 8;
    });

    return offsetTop;
  }

  calcOffsetForAlerts(): void {
    let offsetTop = 0;
    this.currentToasts.forEach((item) => {
      item.style.top = `${offsetTop}px`;
      offsetTop += this.getToastHeight(item);
    });
  }

  showAlert(alert: IAlert): void {
    this.prepareAlert(alert)
      .pipe(
        take(1),
        concatMap((toast) => {
          const id = v4();
          toast.id = id;

          this.currentToasts.push(toast);
          this.calcOffsetForAlerts();

          void toast.present();

          return from(toast.onDidDismiss()).pipe(map(() => id));
        }),
      )
      .subscribe((dismissedAlertId) => {
        this.currentToasts = this.currentToasts.filter(item => item.id !== dismissedAlertId);
        this.calcOffsetForAlerts();
        this.service.dismissById(dismissedAlertId);
      });
  }

  private prepareAlert(alert: IAlert): Observable<HTMLIonToastElement> {
    const message: string | IonicSafeString = `
      <span><strong>${alert.heading}</strong> ${alert.body}</span>
    `;

    const buttons: ToastButton[] = [];

    if (alert.actions) {
      alert.actions.forEach((action) => {
        buttons.push({
          role: 'button',
          text: action.label,
          handler: () => {
            action.action();
          },
        });
      });
    }

    buttons.push({
      role: 'cancel',
      icon: close,
    });

    return from(this.toastController.create({
      message,
      buttons,
      color: alert.type,
      duration: alert.duration ?? this.service.alertDuration,
      translucent: false,
      keyboardClose: false,
      cssClass: ['ion-alert', alert.type],
      position: 'top',
    }));
  }
}
